import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { createCrmBillOperation } from '../operations/create-crm-bill-operation';
import globalSettingsBranches from '../../../../../../../stores/global-settings-branches';
import { Dropdown } from 'primereact/dropdown';
import { CrmReceiptDetailsPanel } from '../../../components/crm-receipt-details-panel';

type BillServiceItem = {
  serviceName: string;
  units: number | null;
  unitPrice: number | null;
};

interface AddCrmBillDialogProps {
  visible: boolean;
  accountId: number | null;
  onHide: () => void;
  onSuccess: () => void;
  /** CRM account display name — default for «לכבוד» (recipient on receipt) */
  defaultReceiptRecipientName?: string;
}

export const AddCrmBillDialog: React.FC<AddCrmBillDialogProps> = ({
  visible,
  accountId,
  onHide,
  onSuccess,
  defaultReceiptRecipientName = '',
}) => {
  const [items, setItems] = useState<BillServiceItem[]>([
    { serviceName: '', units: 1, unitPrice: null },
  ]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);
  const [receiptRecipientName, setReceiptRecipientName] = useState('');
  const [receiptIssuedFor, setReceiptIssuedFor] = useState('');
  const [receiptNote, setReceiptNote] = useState('');

  const branches = globalSettingsBranches.branches ?? [];
  const defaultBranchId = useMemo(() => {
    const idx = globalSettingsBranches.currentBranchIndex;
    if (typeof idx === 'number' && idx >= 0 && idx < branches.length) {
      return branches[idx]?.id ?? null;
    }
    return branches.length === 1 ? branches[0].id : null;
  }, [branches]);

  useEffect(() => {
    if (!visible) return;
    setSelectedBranchId((prev) => prev ?? defaultBranchId);
  }, [visible, defaultBranchId]);

  useEffect(() => {
    if (!visible) return;
    setReceiptRecipientName(defaultReceiptRecipientName.trim().slice(0, 100));
    setReceiptIssuedFor('');
    setReceiptNote('');
  }, [visible, defaultReceiptRecipientName]);

  const totalAmount = useMemo(() => {
    return items.reduce((sum, item) => {
      const units = typeof item.units === 'number' ? item.units : 0;
      const unitPrice = typeof item.unitPrice === 'number' ? item.unitPrice : 0;
      return sum + units * unitPrice;
    }, 0);
  }, [items]);

  const isValid = useMemo(() => {
    if (!accountId) return false;
    if (!items || items.length < 1) return false;
    const allValidItems = items.every(
      (i) =>
        i.serviceName.trim().length > 0 &&
        typeof i.units === 'number' &&
        i.units >= 1 &&
        typeof i.unitPrice === 'number' &&
        i.unitPrice >= 0,
    );
    return allValidItems && totalAmount >= 0.01;
  }, [accountId, items, totalAmount]);

  const reset = () => {
    setItems([{ serviceName: '', units: 1, unitPrice: null }]);
    setSelectedBranchId(null);
    setReceiptRecipientName('');
    setReceiptIssuedFor('');
    setReceiptNote('');
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onHide();
  };

  const addItem = () => {
    setItems((prev) => [
      ...prev,
      { serviceName: '', units: 1, unitPrice: null },
    ]);
  };

  const removeItem = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateItem = (idx: number, patch: Partial<BillServiceItem>) => {
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    );
  };

  const handleApprove = async () => {
    if (!accountId || !isValid || loading) return;

    const branchIdToSend = selectedBranchId ?? defaultBranchId;
    /*if (!branchIdToSend) {
      toast.warn('יש לבחור שלוחה');
      return;
    }*/

    await createCrmBillOperation(
      accountId,
      {
        branchId: branchIdToSend,
        amount: Number(totalAmount.toFixed(2)),
        services: items.map((i) => ({
          serviceName: i.serviceName.trim(),
          units: i.units as number,
          unitPrice: Number((i.unitPrice as number).toFixed(2)),
        })),
        recipientName: receiptRecipientName,
        issuedFor: receiptIssuedFor,
        note: receiptNote,
      },
      setLoading,
      () => {
        handleClose();
        onSuccess();
      },
    );
  };

  return (
    <Dialog
      header="הוספת חשבון"
      visible={visible}
      onHide={handleClose}
      style={{ width: '760px', maxWidth: '95vw', direction: 'rtl' }}
      modal
      closable={!loading}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            label="הוסף פריט"
            icon="pi pi-plus"
            onClick={addItem}
            disabled={loading}
            severity="secondary"
            outlined
          />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              label="ביטול"
              icon="pi pi-times"
              severity="secondary"
              text
              onClick={handleClose}
              disabled={loading}
            />
            <Button
              label={loading ? 'שומר...' : 'אישור'}
              icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
              onClick={handleApprove}
              disabled={!isValid || loading}
              loading={loading}
            />
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <CrmReceiptDetailsPanel
          recipientName={receiptRecipientName}
          onRecipientNameChange={setReceiptRecipientName}
          issuedFor={receiptIssuedFor}
          onIssuedForChange={setReceiptIssuedFor}
          note={receiptNote}
          onNoteChange={setReceiptNote}
          loading={loading}
          kicker="יוצגו במסמך הקבלה לאחר יצירת החשבונית"
          infoText="הפרטים יודפסו בקבלה. «לכבוד» ו-«עבור» — עד 100 תווים כל אחד; ברירת מחדל ל«לכבוד»: שם החשבון ב-CRM. «הערה» — אופציונלי, עד 200 תווים."
          idPrefix="crm-bill-receipt"
        />

        <div className="p-field">
          <label
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 600,
            }}
          >
            שלוחה
          </label>
          <Dropdown
            value={selectedBranchId}
            options={branches.map((b) => ({
              label: b.branchName,
              value: b.id,
            }))}
            onChange={(e) =>
              setSelectedBranchId((e.value ?? null) as number | null)
            }
            optionLabel="label"
            optionValue="value"
            style={{ width: '100%' }}
            disabled={loading}
            placeholder="בחר שלוחה (אופציונלי)"
          />
        </div>

        {items.map((item, idx) => (
          <div
            key={idx}
            style={{
              border: '1px solid var(--surface-200)',
              borderRadius: '8px',
              padding: '12px',
              background: 'var(--surface-0)',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr auto',
                gap: '0.75rem',
                alignItems: 'end',
              }}
            >
              <div className="p-field">
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                  שם שירות *
                </label>
                <InputText
                  value={item.serviceName}
                  onChange={(e) =>
                    updateItem(idx, { serviceName: e.target.value })
                  }
                  style={{ width: '100%' }}
                  disabled={loading}
                />
              </div>

              <div className="p-field">
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                  כמות *
                </label>
                <InputNumber
                  value={item.units}
                  onValueChange={(e) =>
                    updateItem(idx, { units: e.value ?? null })
                  }
                  min={1}
                  useGrouping={false}
                  style={{ width: '100%' }}
                  disabled={loading}
                />
              </div>

              <div className="p-field">
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                  מחיר ליחידה *
                </label>
                <InputNumber
                  value={item.unitPrice}
                  onValueChange={(e) =>
                    updateItem(idx, { unitPrice: e.value ?? null })
                  }
                  mode="currency"
                  currency="ILS"
                  locale="he-IL"
                  min={0}
                  maxFractionDigits={2}
                  style={{ width: '100%', direction: 'ltr' }}
                  inputStyle={{ direction: 'rtl', textAlign: 'right' }}
                  disabled={loading}
                />
              </div>

              <Button
                icon="pi pi-trash"
                text
                severity="danger"
                onClick={() => removeItem(idx)}
                disabled={loading || items.length === 1}
                tooltip={
                  items.length === 1 ? 'חייב להיות לפחות פריט אחד' : 'מחיקה'
                }
              />
            </div>

            <div style={{ marginTop: '10px', color: '#6b7280' }}>
              סה״כ פריט:{' '}
              {(
                (typeof item.units === 'number' ? item.units : 0) *
                (typeof item.unitPrice === 'number' ? item.unitPrice : 0)
              ).toFixed(2)}
            </div>
          </div>
        ))}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            fontWeight: 700,
            fontSize: '1.1rem',
          }}
        >
          סה״כ חשבונית: {totalAmount.toFixed(2)}
        </div>
      </div>
    </Dialog>
  );
};
